import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';

import { Config } from 'config';

import { isOnboardingPath } from './onboardingFragment';

const { sentry, logRocketApp } = Config;
const { dsn, release, environment } = sentry;

// Any URL with an /employees path segment (lists, one employee, /me, Show,
// and /events/:id/employees).
const EMPLOYEE_URL = /\/employees(?:[/?#]|$)/;

// WP7 (C8): the Support frame's sign-in token travels in this answer.
const SSO_TOKEN_URL = /\/support\/sso-token(?:[/?#]|$)/;

// Integrations "Claude API" (31 Oct 2026): Replace sends the key in the
// body. Neither LogRocket nor Sentry keeps this URL's bodies.
export const CLAUDE_KEY_URL = /\/research-api-key(?:[/?#]|$)/;

const SECRET_BODY_URLS = [SSO_TOKEN_URL, CLAUDE_KEY_URL];

const hasSecretBody = (url?: string) =>
  SECRET_BODY_URLS.some((pattern) => pattern.test(url ?? ''));

export const dropEmployeeBody = <T extends { url?: string; body?: unknown }>(
  item: T
): T =>
  EMPLOYEE_URL.test(item.url ?? '') || hasSecretBody(item.url)
    ? { ...item, body: undefined }
    : item;

// LogRocket's response may come without its url: the sso-token and key
// answers are matched to their request by reqId as well.
const secretRequestIds = new Set<string>();

export const sanitizeRequest = <
  T extends { url?: string; reqId?: string; body?: unknown },
>(
  request: T
): T => {
  if (request.reqId && hasSecretBody(request.url)) {
    secretRequestIds.add(request.reqId);
  }
  return dropEmployeeBody(request);
};

export const sanitizeResponse = <
  T extends { url?: string; reqId?: string; body?: unknown },
>(
  response: T
): T =>
  response.reqId && secretRequestIds.delete(response.reqId)
    ? { ...response, body: undefined }
    : dropEmployeeBody(response);

// The rep's onboarding requests carry the link token, the 6-digit code and,
// later, tax and bank details: Sentry never keeps their bodies.
const ONBOARDING_API_URL = /\/api\/v1\/onboarding(?:[/?#]|$)/;

export const dropOnboardingRequestBody = <
  T extends { request?: { url?: string; data?: unknown } },
>(
  event: T
): T =>
  event.request &&
  (ONBOARDING_API_URL.test(event.request.url ?? '') || isOnboardingPath())
    ? { ...event, request: { ...event.request, data: undefined } }
    : event;

type SentryRequest = { url?: string; data?: unknown };
type SentryCrumb = { data?: { [key: string]: unknown } | undefined };

const crumbUrl = (crumb: SentryCrumb) =>
  typeof crumb.data?.url === 'string' ? crumb.data.url : '';

/** No breadcrumb of a Claude key call (it would name its body's size). */
export const dropClaudeKeyBreadcrumb = <T extends SentryCrumb>(
  crumb: T
): T | null => (CLAUDE_KEY_URL.test(crumbUrl(crumb)) ? null : crumb);

/**
 * A Sentry event: no request body for the Claude key URL, and none of its
 * breadcrumbs (any event, e.g. an error just after a Replace).
 */
export const dropClaudeKeyFromEvent = <
  T extends { request?: SentryRequest; breadcrumbs?: SentryCrumb[] },
>(
  event: T
): T => {
  const breadcrumbs = event.breadcrumbs?.filter((crumb) =>
    Boolean(dropClaudeKeyBreadcrumb(crumb))
  );
  const request =
    event.request && CLAUDE_KEY_URL.test(event.request.url ?? '')
      ? { ...event.request, data: undefined }
      : event.request;
  return {
    ...event,
    ...(event.request ? { request } : {}),
    ...(event.breadcrumbs ? { breadcrumbs } : {}),
  };
};

export const setupMonitoring = () => {
  // On the rep's onboarding page (plan 08 §2 [B1-1]): no LogRocket and no
  // Sentry Replay; Sentry errors only, without request bodies.
  const isOnboarding = isOnboardingPath();
  const shouldInitSentry = Boolean(dsn && release);
  const shouldInitLogRocket = Boolean(logRocketApp && release) && !isOnboarding;

  if (shouldInitSentry) {
    try {
      Sentry.init({
        dsn,
        release,
        environment,
        enableLogs: true,
        sendDefaultPii: true,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: isOnboarding ? 0 : 0.1,
        replaysOnErrorSampleRate: isOnboarding ? 0 : 1.0,
        beforeBreadcrumb: (crumb) => dropClaudeKeyBreadcrumb(crumb),
        beforeSend: (event) =>
          dropClaudeKeyFromEvent(dropOnboardingRequestBody(event)),
        integrations: [
          Sentry.browserTracingIntegration(),
          ...(isOnboarding
            ? []
            : [
                Sentry.replayIntegration({
                  maskAllText: false,
                  blockAllMedia: false,
                }),
              ]),
        ],
        ignoreErrors: [
          /An error has occurred and we could not establish a connection to your Clover device/i,
          /Previous payment attempt failed\. Please retry again/i,
          /Wrong approval password, try another one/i,
          /Request already in progress using method RESET_DEVICE_RESPONSE/i,
          /User is not a team member for this event/i,
        ],
      });
    } catch (error) {
      console.error(`[Setup Sentry]: ${error}`);
    }
  }

  if (shouldInitLogRocket) {
    try {
      LogRocket.init(logRocketApp, {
        release,
        console: {
          shouldAggregateConsoleErrors: true,
        },
        // Employee requests and responses can carry bank account numbers
        // (onboarding Step 1), the sso-token answer the Support frame's
        // sign-in token, and a Claude key Replace the key: never record
        // their bodies.
        network: {
          requestSanitizer: sanitizeRequest,
          responseSanitizer: sanitizeResponse,
        },
      });

      LogRocket.getSessionURL((sessionURL) => {
        if (sessionURL) {
          Sentry.setContext('LogRocket', {
            sessionURL: sessionURL,
            timestamp: new Date().toISOString(),
          });

          Sentry.setTag('logRocketSession', 'active');
        }
      });
    } catch (error) {
      console.error(`[Setup LogRocket]: ${error}`);
    }
  }
};
